// Static/free deployment config.
// The paid/public-edition layer is disabled so the app can run as a plain static site.
window.PUBLIC_APP_CONFIG = (() => {
    const localHostname = String(window.location?.hostname || '').trim().toLowerCase();
    const isLocalB2Canary = localHostname === 'localhost' || localHostname === '127.0.0.1';
    const relayBaseUrl = isLocalB2Canary
        ? 'https://memorakey-staging.fadi-sameer-593.workers.dev'
        : 'https://relay.memoraful.com';

    return {
        enabled: false,
        /* Without a personal key: quizzes and Ask Memoraful use the shared relay.
           The eager feature bridge grants other modules to personal-key users and sample demos. */
        productAccess: Object.freeze({ freeTierLimit: true }),
        aiRelayUrl: relayBaseUrl,
        relaySessionUrl: `${relayBaseUrl}/v1/relay-session`,
        relayTurnstileSiteKey: isLocalB2Canary
            ? '0x4AAAAAAEeUP7KXHYwYdHUx'
            : '0x4AAAAAAEbye8sHPHcnC3Vj',
        aiRelayLimits: Object.freeze({
            maxRequestBytes: 12 * 1024 * 1024,
            maxInlineSourceBytes: 8 * 1024 * 1024
        }),
        // Production and localhost both use the B2 source transport. Localhost
        // remains isolated to the staging Worker; production uses relay.memoraful.com.
        quizGenB2TransportEnabled: true,
        // Enabled after the closed staging route, real-slide render, and
        // parent-operation accounting gates passed.
        quizGenVisionQuickStartEnabled: true,
        // Imagen 4 has no free tier. Enable only for API keys backed by a billed project.
        guidedDebriefImagenEnabled: false,
        // Arcade interactive walkthrough URL for getting a free Google AI Studio API key
        geminiApiKeyArcadeDemoUrl: 'https://demo.arcade.software/YOJGulYuCRiGOZJXXuNb?embed&show_copy_link=false'
    };
})();

// Global transparent AI relay bridge
(function () {
    const relayUrl = window.PUBLIC_APP_CONFIG?.aiRelayUrl;
    if (!relayUrl) return;

    const RELAY_TOKEN = '__memora_relay__';
    let relaySessionToken = '';
    let relaySessionExpiresAtMs = 0;
    let relaySessionPromise = null;
    let relaySessionGeneration = 0;
    const relayControlResponseSessionTokens = new WeakMap();
    const sourceUploadSlotSessionTokens = new WeakMap();
    const sourceUploadSlotSessionGenerations = new WeakMap();
    const sourceUploadDocumentDigests = new WeakMap();
    let turnstileLoadPromise = null;
    let turnstileTokenPromise = null;
    // An invisible check normally answers in a few seconds but sometimes never calls back,
    // which left generation (and its Cancel) waiting minutes. Each attempt gets a deadline,
    // including interactive challenges: their completion callback is not guaranteed.
    const TURNSTILE_ATTEMPT_MS = 20000;
    const TURNSTILE_ATTEMPTS = 2;
    const relayTimers = typeof setTimeout === 'function' && typeof clearTimeout === 'function'
        ? { set: (run, ms) => setTimeout(run, ms), clear: (id) => clearTimeout(id) }
        : null;
    const relayLimits = Object.freeze({
        maxRequestBytes: Math.max(1, Number(window.PUBLIC_APP_CONFIG?.aiRelayLimits?.maxRequestBytes) || 12 * 1024 * 1024),
        maxInlineSourceBytes: Math.max(1, Number(window.PUBLIC_APP_CONFIG?.aiRelayLimits?.maxInlineSourceBytes) || 8 * 1024 * 1024)
    });
    const relayOperationIdPattern = /^[A-Za-z0-9._~-]{16,128}$/;
    // Vision quick-start shares the parent quiz-gen operation; it is not a
    // separately settleable capability and therefore is intentionally absent.
    const relayOperationCapabilities = new Set(['ask', 'learn', 'quiz-gen']);
    const relayPurposes = new Set(['discrepancy-verification']);
    const relayAskModelSelections = new Set([
        'gemini-3.5-flash-lite',
        'gemma-4-31b-it',
        'groq-qwen'
    ]);

    const getRelayOperationIdentity = (operation) => {
        const operationId = String(operation?.idempotencyKey || operation?.operationId || '').trim();
        return relayOperationIdPattern.test(operationId) ? operationId : '';
    };

    const notifyAnonymousCapabilityExhausted = async (response) => {
        if (response?.status !== 429) return;
        const payload = await response.clone().json().catch(() => ({}));
        if (payload?.error?.status !== 'ANONYMOUS_CAPABILITY_EXHAUSTED') return;
        try {
            window.dispatchEvent?.(new CustomEvent('memora:anonymous-capability-exhausted', {
                detail: {
                    capability: String(payload?.quota?.capability || response.headers?.get?.('x-memora-capability') || '').trim(),
                    limit: Number(payload?.quota?.limit || response.headers?.get?.('x-memora-capability-limit') || 0) || 0,
                    remaining: Number(payload?.quota?.remaining || response.headers?.get?.('x-memora-capability-remaining') || 0) || 0,
                    resetAt: Number(payload?.quota?.resetAt || response.headers?.get?.('x-memora-capability-reset') || 0) || 0
                }
            }));
        } catch (_) {}
    };

    const createMemoraRelayOperation = (capability = '') => {
        const normalizedCapability = String(capability || '').trim().toLowerCase();
        if (!relayOperationCapabilities.has(normalizedCapability)) return null;
        const operationId = String(globalThis.crypto?.randomUUID?.() || '').trim();
        if (!relayOperationIdPattern.test(operationId)) return null;
        let settledOutcome = '';
        let settlementPromise = null;
        let observedClientSuccess = false;
        const settle = (outcome = 'failure') => {
            const normalizedOutcome = String(outcome || '').trim().toLowerCase();
            if (!['success', 'failure'].includes(normalizedOutcome)) return Promise.resolve(null);
            if (settledOutcome) return Promise.resolve({ status: settledOutcome, settled: false });
            if (settlementPromise) return settlementPromise;
            settlementPromise = (async () => {
                if (typeof window.ensureMemoraRelaySession !== 'function') {
                    throw new Error('The anonymous relay session is unavailable.');
                }
                await window.ensureMemoraRelaySession();
                const baseUrl = String(window.PUBLIC_APP_CONFIG?.aiRelayUrl || '').replace(/\/$/, '');
                const operationUrl = `${baseUrl}/v1/relay-operation`;
                const send = async (token) => {
                    const headers = new Headers({
                        'Content-Type': 'application/json',
                        'Idempotency-Key': operationId,
                        'X-Memora-Capability': normalizedCapability
                    });
                    if (token) headers.set('x-relay-session', token);
                    return origFetch.call(window, operationUrl, {
                        method: 'POST',
                        credentials: 'include',
                        cache: 'no-store',
                        headers,
                        body: JSON.stringify({ outcome: normalizedOutcome })
                    });
                };
                const requestSessionToken = relaySessionToken;
                let response = await send(requestSessionToken);
                if (await isRelaySessionChallenge(response) && requestSessionToken === relaySessionToken) {
                    clearRelaySession(requestSessionToken);
                    await window.ensureMemoraRelaySession();
                    response = await send(relaySessionToken);
                }
                if (!response.ok) {
                    const payload = await response.clone().json().catch(() => ({}));
                    const error = new Error(payload?.error?.message || `Anonymous operation settlement failed (${response.status || 0}).`);
                    error.status = Number(response.status) || 0;
                    error.code = String(payload?.error?.status || '').trim() || 'RELAY_OPERATION_SETTLEMENT_FAILED';
                    throw error;
                }
                const payload = await response.json().catch(() => ({}));
                settledOutcome = normalizedOutcome;
                return payload?.operation || payload;
            })().finally(() => {
                settlementPromise = null;
            });
            return settlementPromise;
        };
        return Object.freeze({
            capability: normalizedCapability,
            operationId,
            idempotencyKey: operationId,
            settle,
            markSuccess() {
                observedClientSuccess = true;
            },
            hasSuccess() {
                return observedClientSuccess;
            }
        });
    };

    window.createMemoraRelayOperation = createMemoraRelayOperation;
    window.getMemoraRelayOperationId = getRelayOperationIdentity;

    const getRelayRequestByteLength = (body) => {
        const text = typeof body === 'string' ? body : JSON.stringify(body ?? '');
        return new TextEncoder().encode(text).byteLength;
    };

    const getDecodedBase64ByteLength = (value) => {
        const normalized = String(value || '').replace(/\s+/g, '');
        if (!normalized || !/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)) return 0;
        const padding = normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0;
        return Math.max(0, Math.floor(normalized.length * 3 / 4) - padding);
    };

    const getInlineSourceByteLength = (value) => {
        if (!value || typeof value !== 'object') return 0;
        if (Array.isArray(value)) return value.reduce((total, entry) => total + getInlineSourceByteLength(entry), 0);
        return Object.entries(value).reduce((total, [key, child]) => {
            const normalizedKey = key.replace(/[-_]/g, '').toLowerCase();
            if (normalizedKey === 'inlinedata' && child && typeof child === 'object') {
                return total + getDecodedBase64ByteLength(child.data);
            }
            return total + getInlineSourceByteLength(child);
        }, 0);
    };

    const createRelayPayloadTooLargeResponse = (message) => new Response(JSON.stringify({
        error: {
            code: 413,
            status: 'PAYLOAD_TOO_LARGE',
            message
        }
    }), {
        status: 413,
        headers: {
            'Cache-Control': 'no-store',
            'Content-Type': 'application/json; charset=utf-8',
            'X-Relay-Client-Rejected': 'payload-size'
        }
    });

    window.MemoraRelayLimits = relayLimits;
    window.getMemoraRelayRequestByteLength = getRelayRequestByteLength;

    // Keep access to the raw value so the relay sentinel never looks like a
    // user-saved key to settings or other UI surfaces.
    const origGetItem = Storage.prototype.getItem;
    const getRawStoredGeminiApiKey = () => {
        try {
            return String(origGetItem.call(window.localStorage, 'gemini_api_key') || '').trim();
        } catch (_) {
            return '';
        }
    };

    // Treat any non-empty value other than the relay sentinel as a user key.
    // Google remains the authority on whether the key is valid.
    window.isUserCustomGeminiApiKeyValid = function (key) {
        const raw = typeof key === 'string' ? key.trim() : getRawStoredGeminiApiKey();
        return !!raw && raw !== RELAY_TOKEN;
    };

    window.getUserCustomGeminiAPIKey = function () {
        const raw = getRawStoredGeminiApiKey();
        return window.isUserCustomGeminiApiKeyValid(raw) ? raw : '';
    };

    // 1. Intercept localStorage for API key fallback
    Storage.prototype.getItem = function (key) {
        const val = origGetItem.call(this, key);
        if (this === window.localStorage && key === 'gemini_api_key') {
            return window.getUserCustomGeminiAPIKey() || RELAY_TOKEN;
        }
        return val;
    };

    // 2. Transparent fetch interceptor for Google Gemini API calls
    const origFetch = window.fetch;
    const notifyRelaySessionChanged = () => {
        relaySessionGeneration += 1;
        try { window.dispatchEvent(new CustomEvent('memora:relay-session-changed')); } catch (_) {}
    };
    const clearRelaySession = (expectedToken = '') => {
        if (expectedToken && relaySessionToken && expectedToken !== relaySessionToken) return;
        const changed = !!relaySessionToken || relaySessionExpiresAtMs > 0;
        relaySessionToken = '';
        relaySessionExpiresAtMs = 0;
        if (changed) notifyRelaySessionChanged();
    };

    window.setMemoraRelaySessionToken = function (token, expiresAt = 0) {
        const nextToken = typeof token === 'string' ? token.trim() : '';
        const nextExpiresAtMs = Math.max(0, Number(expiresAt || 0) * 1000);
        const changed = nextToken !== relaySessionToken || nextExpiresAtMs !== relaySessionExpiresAtMs;
        relaySessionToken = nextToken;
        relaySessionExpiresAtMs = nextExpiresAtMs;
        if (changed) notifyRelaySessionChanged();
        return relaySessionToken;
    };

    const exchangeRelaySession = async (turnstileToken) => {
        const sessionUrl = window.PUBLIC_APP_CONFIG?.relaySessionUrl;
        if (!sessionUrl || typeof turnstileToken !== 'string' || !turnstileToken.trim()) {
            throw new Error('A bot-verification token is required to start a relay session.');
        }
        const response = await origFetch.call(this, sessionUrl, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ turnstileToken: turnstileToken.trim() })
        });
        const payload = await response.clone().json().catch(() => ({}));
        if (!response.ok || typeof payload?.sessionToken !== 'string' || !payload.sessionToken) {
            const error = new Error(payload?.error?.message || 'Bot verification failed or expired.');
            error.status = response.status;
            error.code = payload?.error?.status || 'BOT_VERIFICATION_FAILED';
            throw error;
        }
        window.setMemoraRelaySessionToken(payload.sessionToken, payload.expiresAt);
        return payload;
    };

    window.requestMemoraRelaySession = exchangeRelaySession;

    const loadTurnstile = async () => {
        if (window.turnstile && typeof window.turnstile.render === 'function') return window.turnstile;
        if (turnstileLoadPromise) return turnstileLoadPromise;
        turnstileLoadPromise = new Promise((resolve, reject) => {
            const existing = document.querySelector?.('script[data-memora-turnstile]');
            if (existing) {
                existing.addEventListener('load', () => resolve(window.turnstile), { once: true });
                existing.addEventListener('error', () => reject(new Error('Turnstile could not be loaded.')), { once: true });
                return;
            }
            const script = document.createElement?.('script');
            if (!script) {
                reject(new Error('Turnstile is unavailable in this browser.'));
                return;
            }
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            script.dataset.memoraTurnstile = 'true';
            script.onload = () => window.turnstile && typeof window.turnstile.render === 'function'
                ? resolve(window.turnstile)
                : reject(new Error('Turnstile loaded without its API.'));
            script.onerror = () => reject(new Error('Turnstile could not be loaded.'));
            document.head?.appendChild(script);
        }).catch((error) => {
            turnstileLoadPromise = null;
            throw error;
        });
        return turnstileLoadPromise;
    };

    const getTurnstileToken = async () => {
        const siteKey = String(window.PUBLIC_APP_CONFIG?.relayTurnstileSiteKey || '').trim();
        if (!siteKey) throw new Error('Bot verification is not configured for the relay.');
        if (turnstileTokenPromise) return turnstileTokenPromise;
        turnstileTokenPromise = (async () => {
            const turnstile = await loadTurnstile();
            let container = document.getElementById('memora-relay-turnstile');
            if (!container) {
                container = document.createElement('div');
                container.id = 'memora-relay-turnstile';
                container.setAttribute('aria-live', 'polite');
                container.style.position = 'fixed';
                container.style.right = '1rem';
                container.style.bottom = '1rem';
                container.style.zIndex = '2147483000';
                document.body.appendChild(container);
            }
            // Each attempt owns its widget and callbacks. Removing it on settlement prevents
            // a late callback from an expired challenge settling the following attempt.
            const runAttemptWithDeadline = () => new Promise((resolve, reject) => {
                let widgetId = null;
                let timer = null;
                let settled = false;
                const finish = (error, token) => {
                    if (settled) return;
                    settled = true;
                    if (timer !== null) relayTimers?.clear(timer);
                    try { if (widgetId !== null) turnstile.remove?.(widgetId); } catch (_) {}
                    if (error) reject(error);
                    else resolve(token);
                };
                if (relayTimers) timer = relayTimers.set(() => {
                    const error = new Error('Bot verification did not respond. Please try again.');
                    error.memoraTurnstileStalled = true;
                    finish(error);
                }, TURNSTILE_ATTEMPT_MS);
                try {
                    widgetId = turnstile.render(container, {
                        sitekey: siteKey,
                        action: 'memora_relay_session',
                        execution: 'execute',
                        appearance: 'interaction-only',
                        retry: 'never',
                        'refresh-expired': 'manual',
                        'refresh-timeout': 'manual',
                        callback: (token) => finish(null, token),
                        'error-callback': (code) => {
                            finish(new Error(`Bot verification failed${code ? ` (${code})` : ''}.`));
                            return true;
                        },
                        'expired-callback': () => finish(new Error('Bot verification expired.')),
                        'timeout-callback': () => finish(new Error('Bot verification timed out.'))
                    });
                    turnstile.execute(widgetId);
                } catch (error) { finish(error); }
            });
            let lastError = null;
            for (let attempt = 0; attempt < TURNSTILE_ATTEMPTS; attempt += 1) {
                try {
                    return await runAttemptWithDeadline();
                } catch (error) {
                    lastError = error;
                    if (!error?.memoraTurnstileStalled) throw error;
                }
            }
            throw lastError;
        })().finally(() => {
            turnstileTokenPromise = null;
        });
        return turnstileTokenPromise;
    };

    window.ensureMemoraRelaySession = async function () {
        if (relaySessionToken && relaySessionExpiresAtMs > Date.now() + 5000) return relaySessionToken;
        if (relaySessionPromise) return relaySessionPromise;
        relaySessionPromise = (async () => {
            const turnstileToken = await getTurnstileToken();
            const payload = await exchangeRelaySession(turnstileToken);
            return payload.sessionToken;
        })().catch((error) => {
            if (error && typeof error === 'object') error.memoraRelaySessionError = true;
            throw error;
        }).finally(() => {
            relaySessionPromise = null;
        });
        return relaySessionPromise;
    };

    // Waits for a relay session without outliving the request that needs it: an aborted
    // request (Cancel) stops waiting at once instead of sitting behind bot verification.
    const relayAbortError = (signal) => signal?.reason
        || Object.assign(new Error('The request was aborted.'), { name: 'AbortError' });
    const awaitRelaySession = (signal) => {
        if (!signal?.addEventListener) return window.ensureMemoraRelaySession();
        if (signal.aborted) return Promise.reject(relayAbortError(signal));
        return new Promise((resolve, reject) => {
            const onAbort = () => reject(relayAbortError(signal));
            signal.addEventListener('abort', onAbort, { once: true });
            Promise.resolve(window.ensureMemoraRelaySession())
                .then(resolve, reject)
                .finally(() => signal.removeEventListener('abort', onAbort));
        });
    };
    const hasLiveRelaySession = () => !!relaySessionToken && relaySessionExpiresAtMs > Date.now() + 5000;

    const getMemoraSourceUploadErrorPayload = async (response) => {
        const payload = await response.clone().json().catch(() => ({}));
        const error = new Error(
            payload?.error?.message
            || payload?.message
            || `Source upload request failed (${response.status || 0}).`
        );
        error.status = Number(response.status) || 0;
        error.code = String(payload?.error?.status || '').trim() || 'SOURCE_UPLOAD_FAILED';
        error.requestId = String(payload?.requestId || '').trim();
        error.sourceUploadStage = error.status === 401 && error.code === 'RELAY_SESSION_REQUIRED'
            ? 'session'
            : 'control';
        return error;
    };

    const isRelaySessionControlChallenge = async (response) => {
        if (response?.status !== 401) return false;
        const payload = await response.clone().json().catch(() => ({}));
        return payload?.error?.status === 'RELAY_SESSION_REQUIRED';
    };

    const isInvalidUploadTicketResponse = async (response) => {
        if (response?.status !== 400) return false;
        const payload = await response.clone().json().catch(() => ({}));
        return payload?.error?.status === 'INVALID_UPLOAD_TICKET';
    };

    const requestMemoraRelayControl = async (
        path,
        init = {},
        { sessionTokenOverride = null, retryOnSessionChallenge = true } = {}
    ) => {
        const url = `${String(window.PUBLIC_APP_CONFIG?.aiRelayUrl || '').replace(/\/$/, '')}/${String(path || '').replace(/^\//, '')}`;
        if (!url || url === '/') throw new Error('The Memora source-upload relay is not configured.');

        await awaitRelaySession(init?.signal);
        const request = {
            ...init,
            method: 'POST',
            credentials: 'include',
            cache: 'no-store'
        };
        const headers = new Headers(init.headers || {});
        headers.set('Content-Type', 'application/json');
        const requestSessionToken = sessionTokenOverride ?? relaySessionToken;
        if (requestSessionToken) headers.set('x-relay-session', requestSessionToken);
        request.headers = headers;

        let response = await origFetch.call(window, url, request);
        if (!retryOnSessionChallenge || !(await isRelaySessionControlChallenge(response))) {
            relayControlResponseSessionTokens.set(response, requestSessionToken);
            return response;
        }

        // A control request without a pinned ticket session may have raced
        // another request that already refreshed the relay session. Reuse the
        // newer token instead of surfacing a stale 401 to the caller. A
        // slot-bound request never reaches this branch because its callers set
        // retryOnSessionChallenge=false; its ticket must be reuploaded under
        // the new session rather than replayed here.
        if (requestSessionToken !== relaySessionToken) {
            if (init?.signal?.aborted) return response;
            try { await response.body?.cancel?.(); } catch (_) {}
            // The other request may have cleared the old token but still be
            // waiting for the shared replacement-session exchange. Wait for
            // that exchange (or reuse its promise) before replaying; an
            // immediate replay without a header would just produce another
            // session challenge.
            await window.ensureMemoraRelaySession();
            const replayHeaders = new Headers(headers);
            if (relaySessionToken) replayHeaders.set('x-relay-session', relaySessionToken);
            response = await origFetch.call(window, url, { ...request, headers: replayHeaders });
            relayControlResponseSessionTokens.set(response, relaySessionToken);
            return response;
        }

        clearRelaySession(requestSessionToken);
        if (init?.signal?.aborted) return response;
        await window.ensureMemoraRelaySession();
        const replayHeaders = new Headers(headers);
        if (relaySessionToken) replayHeaders.set('x-relay-session', relaySessionToken);
        response = await origFetch.call(window, url, { ...request, headers: replayHeaders });
        relayControlResponseSessionTokens.set(response, relaySessionToken);
        return response;
    };

    // SDK-compatible relay Live connection for Quiz Gen and keyless Ask. Shared keys never
    // enter the browser; the operation's capability picks the worker's tool and quota rules.
    window.connectMemoraQuizLive = async ({ model, config, callbacks = {}, signal = null, relayOperation = null } = {}) => {
        const operationId = getRelayOperationIdentity(relayOperation);
        const capability = relayOperation?.capability;
        if (!operationId || (capability !== 'quiz-gen' && capability !== 'ask')) throw new Error('Relay Live requires a quiz or Ask operation.');
        const response = await requestMemoraRelayControl('/v1/quiz-live', {
            headers: { 'idempotency-key': operationId },
            body: JSON.stringify(capability === 'ask' ? { model, config, capability } : { model, config }), signal
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            const error = new Error(payload?.message || 'Quiz Live could not connect.');
            error.liveCode = payload?.status || 'live_error';
            error.status = response.status;
            throw error;
        }
        const url = new URL(payload.url);
        const relayUrl = new URL(window.PUBLIC_APP_CONFIG.aiRelayUrl);
        if (url.protocol !== 'wss:' || url.host !== relayUrl.host || url.pathname !== '/v1/quiz-live') {
            throw new Error('Invalid Quiz Live connection URL.');
        }
        if (signal?.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
        return new Promise((resolve, reject) => {
            const socket = new WebSocket(url.toString());
            let ready = false;
            let settled = false;
            const send = message => {
                if (socket.readyState !== WebSocket.OPEN || !ready) throw new Error('Quiz Live is not connected.');
                socket.send(JSON.stringify(message));
            };
            const session = {
                sendClientContent: content => send({ clientContent: content }),
                sendToolResponse: response => send({ toolResponse: response }),
                close: () => { clearTimeout(timer); signal?.removeEventListener?.('abort', abort); socket.close(); }
            };
            const fail = error => {
                if (!settled) { settled = true; reject(error); }
                session.close();
            };
            const abort = () => fail(signal.reason || new DOMException('Aborted', 'AbortError'));
            const timer = setTimeout(() => fail(new Error('Quiz Live connection timed out.')), 20_000);
            signal?.addEventListener?.('abort', abort, { once: true });
            socket.addEventListener('message', async event => {
                try {
                    const raw = typeof event.data === 'string' ? event.data : await event.data.text();
                    const message = JSON.parse(raw);
                    if (message.setupComplete && !ready) {
                        ready = true; settled = true; clearTimeout(timer); resolve(session);
                    }
                    callbacks.onmessage?.(message);
                } catch (error) { callbacks.onerror?.(error); fail(error); }
            });
            socket.addEventListener('error', () => {
                const error = new Error('Quiz Live connection failed.');
                callbacks.onerror?.(error); fail(error);
            });
            socket.addEventListener('close', event => {
                clearTimeout(timer); signal?.removeEventListener?.('abort', abort);
                callbacks.onclose?.(event);
                if (!settled) { settled = true; reject(new Error('Quiz Live closed before setup completed.')); }
            });
        });
    };

    const normalizeMemoraSourceUploadSlot = (payload) => {
        const uploadTicket = String(payload?.uploadTicket || '').trim();
        const uploadUrl = String(payload?.uploadUrl || '').trim();
        const expiresAt = Number(payload?.expiresAt) || 0;
        if (!uploadTicket || !uploadUrl || !Number.isFinite(expiresAt) || expiresAt <= 0) {
            throw new Error('The source-upload relay returned an invalid upload slot.');
        }
        return Object.freeze({ uploadTicket, uploadUrl, expiresAt });
    };

    const prefetchMemoraSourceUploadSlot = async ({ mimeType = 'application/pdf', signal = null } = {}) => {
        const normalizedMimeType = String(mimeType || '').trim().toLowerCase();
        if (normalizedMimeType !== 'application/pdf') {
            throw new Error('Only PDF source uploads are supported.');
        }
        const response = await requestMemoraRelayControl('/v1/source-upload/prefetch', {
            signal: signal || undefined,
            body: JSON.stringify({ mimeType: normalizedMimeType })
        });
        if (!response.ok) throw await getMemoraSourceUploadErrorPayload(response);
        const slot = normalizeMemoraSourceUploadSlot(await response.json().catch(() => ({})));
        sourceUploadSlotSessionTokens.set(slot, relayControlResponseSessionTokens.get(response) || relaySessionToken);
        sourceUploadSlotSessionGenerations.set(slot, relaySessionGeneration);
        return slot;
    };

    const putMemoraSourceUpload = async (slot, body, { signal = null, onStarted = null, onCompleted = null } = {}) => {
        const uploadUrl = String(slot?.uploadUrl || '').trim();
        if (!uploadUrl || !(body instanceof Blob)) {
            throw new Error('A PDF upload slot and source body are required.');
        }
        if (typeof onStarted === 'function') onStarted();
        // Measurement only: a SHA-256 of the exact bytes uploaded, used to count how
        // often the same lecture is generated twice. Started beside the PUT rather
        // than before it, because the upload must begin the moment the body is ready.
        // A failure here must never fail an upload, so it resolves to an empty digest.
        if (slot && typeof crypto?.subtle?.digest === 'function') {
            sourceUploadDocumentDigests.set(slot, body.arrayBuffer()
                .then((bytes) => crypto.subtle.digest('SHA-256', bytes))
                .then((digest) => Array.from(new Uint8Array(digest))
                    .map((byte) => byte.toString(16).padStart(2, '0'))
                    .join(''))
                .catch(() => ''));
        }
        let response;
        try {
            response = await origFetch.call(window, uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/pdf' },
                body,
                signal: signal || undefined
            });
        } catch (error) {
            if (error && typeof error === 'object') error.sourceUploadStage = 'put';
            throw error;
        }
        if (!response.ok) {
            const error = new Error(`Source upload failed (${response.status || 0}).`);
            error.status = Number(response.status) || 0;
            error.sourceUploadStage = 'put';
            throw error;
        }
        if (typeof onCompleted === 'function') onCompleted();
        return true;
    };

    const commitMemoraSourceUpload = async (slot, { signal = null } = {}) => {
        const uploadTicket = String(slot?.uploadTicket || '').trim();
        if (!uploadTicket) throw new Error('A source-upload ticket is required.');
        const documentHash = await (sourceUploadDocumentDigests.get(slot) || Promise.resolve(''));
        let response = await requestMemoraRelayControl('/v1/source-upload/commit', {
            signal: signal || undefined,
            body: JSON.stringify(documentHash ? { uploadTicket, documentHash } : { uploadTicket })
        }, {
            sessionTokenOverride: sourceUploadSlotSessionTokens.get(slot) || null,
            // A ticket is bound to the session that minted it. If that session
            // is challenged, the caller must obtain a new slot and re-upload;
            // replaying this commit under a new session always invalidates it.
            retryOnSessionChallenge: false
        });
        if (documentHash && await isInvalidUploadTicketResponse(response)) {
            // Older Workers enforced the original one-field commit contract
            // even after digest telemetry was added. Retry once with the
            // original bound session so a frontend-first rollout remains
            // compatible without broad retries or session replay.
            response = await requestMemoraRelayControl('/v1/source-upload/commit', {
                signal: signal || undefined,
                body: JSON.stringify({ uploadTicket })
            }, {
                sessionTokenOverride: sourceUploadSlotSessionTokens.get(slot) || null,
                retryOnSessionChallenge: false
            });
        }
        if (!response.ok) throw await getMemoraSourceUploadErrorPayload(response);
        const payload = await response.json().catch(() => ({}));
        const sourceRef = String(payload?.sourceRef || '').trim();
        const size = Number(payload?.size) || 0;
        const mimeType = String(payload?.mimeType || '').trim().toLowerCase();
        const expiresAt = Number(payload?.expiresAt) || 0;
        if (!sourceRef || size <= 0 || mimeType !== 'application/pdf' || expiresAt <= 0) {
            throw new Error('The source-upload relay returned an invalid source reference.');
        }
        return Object.freeze({ sourceRef, size, mimeType, expiresAt });
    };

    const releaseMemoraSourceUploadSlot = async (slot, { signal = null } = {}) => {
        const uploadTicket = String(slot?.uploadTicket || '').trim();
        if (!uploadTicket) throw new Error('A source-upload ticket is required.');
        const response = await requestMemoraRelayControl('/v1/source-upload/release', {
            signal: signal || undefined,
            body: JSON.stringify({ uploadTicket })
        }, {
            sessionTokenOverride: sourceUploadSlotSessionTokens.get(slot) || null,
            retryOnSessionChallenge: false
        });
        if (!response.ok) throw await getMemoraSourceUploadErrorPayload(response);
        return true;
    };

    window.MemoraSourceUploadClient = Object.freeze({
        prefetch: prefetchMemoraSourceUploadSlot,
        put: putMemoraSourceUpload,
        commit: commitMemoraSourceUpload,
        release: releaseMemoraSourceUploadSlot,
        getSessionGeneration(slot = null) {
            return slot ? (sourceUploadSlotSessionGenerations.get(slot) || 0) : relaySessionGeneration;
        }
    });

    const isRelaySessionChallenge = async (response) => {
        if (response?.status !== 401) return false;
        const payload = await response.clone().json().catch(() => ({}));
        return payload?.error?.status === 'RELAY_SESSION_REQUIRED';
    };

    window.fetch = async function (input, init = {}) {
        const requestUrl = typeof input === 'string'
            ? input
            : input instanceof URL
                ? input.toString()
                : (input && typeof input.url === 'string' ? input.url : '');

        if (typeof requestUrl === 'string' && requestUrl.includes('generativelanguage.googleapis.com')) {
            let queryApiKey = '';
            try {
                queryApiKey = new URL(requestUrl).searchParams.get('key') || '';
            } catch (_) {}

            const sourceHeaders = new Headers(input instanceof Request ? input.headers : undefined);
            const overrideHeaders = new Headers(init?.headers || undefined);
            overrideHeaders.forEach((value, key) => sourceHeaders.set(key, value));
            const headerApiKey = sourceHeaders.get('x-goog-api-key') || '';
            const effectiveApiKey = String(queryApiKey || headerApiKey || '').trim();

            // Route through Cloudflare Worker if using relay token or missing key
            if (!effectiveApiKey || effectiveApiKey === RELAY_TOKEN) {
                const modelMatch = requestUrl.match(/\/models\/([^/:?]+):([^/?]+)/i);
                const requestedModel = modelMatch ? modelMatch[1] : '';
                const requestedAction = modelMatch ? modelMatch[2] : (requestUrl.includes('streamGenerateContent') ? 'streamGenerateContent' : 'generateContent');
                const isStream = requestedAction === 'streamGenerateContent' || requestUrl.includes('streamGenerateContent');
                let requestBody = init?.body;

                if (requestBody === undefined && input instanceof Request && !['GET', 'HEAD'].includes(input.method)) {
                    try {
                        requestBody = await input.clone().text();
                    } catch (_) {}
                }

                if (requestBody) {
                    try {
                        const parsed = typeof requestBody === 'string' ? JSON.parse(requestBody) : requestBody;
                        if (parsed && typeof parsed === 'object') {
                            if (!parsed.model && requestedModel) {
                                parsed.model = requestedModel;
                            }
                            if (!parsed.action && requestedAction) {
                                parsed.action = requestedAction;
                            }
                            if (isStream) {
                                parsed.stream = true;
                            }
                            requestBody = JSON.stringify(parsed);
                            const encodedBytes = getRelayRequestByteLength(requestBody);
                            if (encodedBytes > relayLimits.maxRequestBytes) {
                                return createRelayPayloadTooLargeResponse('This request is too large for the free relay. Compress, crop, or split the source, or use a personal Google Gemini API key.');
                            }
                            if (getInlineSourceByteLength(parsed) > relayLimits.maxInlineSourceBytes) {
                                return createRelayPayloadTooLargeResponse('This source is too large for the free relay. Compress, crop, or split it, or use a personal Google Gemini API key.');
                            }
                        }
                    } catch (_) {}
                }

                sourceHeaders.delete('x-goog-api-key');
                sourceHeaders.delete('content-length');
                sourceHeaders.delete('x-memora-capability');
                sourceHeaders.delete('x-memora-ask-model');
                sourceHeaders.delete('x-memora-purpose');
                const relayCapability = String(init?.__memoraRelayCapability || '').trim().toLowerCase();
                if (['ask', 'learn', 'quiz-gen', 'quiz-gen-enrichment', 'quiz-gen-quickstart'].includes(relayCapability)) {
                    sourceHeaders.set('x-memora-capability', relayCapability);
                }
                const relayAskModel = String(init?.__memoraAskModel || '').trim().toLowerCase();
                if (relayCapability === 'ask' && relayAskModelSelections.has(relayAskModel)) {
                    sourceHeaders.set('x-memora-ask-model', relayAskModel);
                }
                const relayPurpose = String(init?.__memoraRelayPurpose || '').trim().toLowerCase();
                if (relayPurposes.has(relayPurpose)) sourceHeaders.set('x-memora-purpose', relayPurpose);
                const relayOperation = init?.__memoraRelayOperation;
                const relayOperationId = getRelayOperationIdentity(relayOperation);
                const sharesQuizGenOperation = relayCapability === 'quiz-gen-quickstart'
                    && relayOperation?.capability === 'quiz-gen';
                if (relayOperationId && (relayOperation?.capability === relayCapability || sharesQuizGenOperation)) {
                    sourceHeaders.set('idempotency-key', relayOperationId);
                }
                // Start the session first. Sending without one only buys a 401 per request
                // (several in a row when a quiz fans out), each followed by the same wait.
                let sessionPreflightFailed = false;
                if (init?.__memoraRelayReplay !== true && !hasLiveRelaySession()) {
                    try {
                        await awaitRelaySession(init?.signal);
                    } catch (error) {
                        if (init?.signal?.aborted) throw error;
                        sessionPreflightFailed = true;
                    }
                }
                if (relaySessionToken) sourceHeaders.set('x-relay-session', relaySessionToken);
                if (sourceHeaders.get('x-user-api-key') === RELAY_TOKEN) {
                    sourceHeaders.delete('x-user-api-key');
                }
                sourceHeaders.set('Content-Type', 'application/json');

                const relayRequestInit = {
                    ...init,
                    method: 'POST',
                    headers: sourceHeaders,
                    body: requestBody,
                    credentials: 'include'
                };
                const requestSessionToken = relaySessionToken;
                const response = await origFetch.call(this, relayUrl, relayRequestInit);
                await notifyAnonymousCapabilityExhausted(response);
                if (init?.__memoraRelayReplay === true || !(await isRelaySessionChallenge(response))) return response;
                if (requestSessionToken === relaySessionToken) clearRelaySession(requestSessionToken);
                if (init?.signal?.aborted || sessionPreflightFailed) return response;
                try {
                    await awaitRelaySession(init?.signal);
                } catch (_) {
                    return response;
                }
                try { await response.body?.cancel?.(); } catch (_) {}
                const replayHeaders = new Headers(sourceHeaders);
                if (relaySessionToken) replayHeaders.set('x-relay-session', relaySessionToken);
                return origFetch.call(this, relayUrl, {
                    ...relayRequestInit,
                    headers: replayHeaders,
                    __memoraRelayReplay: true
                }).then(async (replayedResponse) => {
                    await notifyAnonymousCapabilityExhausted(replayedResponse);
                    return replayedResponse;
                });
            }
        }

        return origFetch.call(this, input, init);
    };

    // 3. Keep API key modal input clean if using relay token
    window.addEventListener('DOMContentLoaded', () => {
        const input = document.getElementById('gemini-api-key-input');
        if (input) {
            input.addEventListener('focus', () => {
                if (input.value === RELAY_TOKEN) {
                    input.value = '';
                }
            });
        }
    });
})();
